import * as Sentry from '@sentry/react';

// Hard-coded Sentry DSN - will be replaced in Gen3
const SENTRY_DSN = 'https://2d8eff15cfd5571f46adec2e2ff8387c@o4510753424605184.ingest.us.sentry.io/4510753865400320';

export function initSentry() {
  if (!SENTRY_DSN) {
    console.warn('Sentry DSN not configured, error tracking disabled');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE || 'development',
    release: import.meta.env.VITE_APP_VERSION || 'unknown',
    
    // Filter out known non-critical errors
    beforeSend(event, hint) {
      const error = hint.originalException;
      
      // Ignore "No current user" errors from Amplify (expected when logged out)
      if (error && typeof error === 'object' && 'message' in error) {
        const message = (error as Error).message;
        if (message === 'No current user' || message === 'The user is not authenticated') {
          return null;
        }
      }
      
      return event;
    },

    // Add default tags
    initialScope: {
      tags: {
        app: 'gen2',
      },
    },
  });
}

export function setOrderContext(orderId: string, additionalContext?: {
  status?: string;
  printManufacturer?: string;
  customerEmail?: string;
}) {
  Sentry.setContext('order', {
    id: orderId,
    ...additionalContext,
  });

  Sentry.setTag('order_id', orderId);

  if (additionalContext?.printManufacturer) {
    Sentry.setTag('manufacturer', additionalContext.printManufacturer);
  }
}

export function setCustomerContext(userData: {
  id?: string;
  email?: string;
  name?: string;
  isAdmin?: boolean;
  isManufacturer?: boolean;
}) {
  Sentry.setUser({
    id: userData.id,
    email: userData.email,
    username: userData.name,
  });

  Sentry.setContext('user_role', {
    isAdmin: userData.isAdmin,
    isManufacturer: userData.isManufacturer,
  });
}

/**
 * Clear user context (call on logout)
 */
export function clearUserContext() {
  Sentry.setUser(null);
}

export function captureError(error: Error | unknown, context?: {
  orderId?: string;
  customerId?: string;
  extra?: Record<string, unknown>;
  level?: Sentry.SeverityLevel;
}) {
  if (context?.orderId) {
    Sentry.setTag('order_id', context.orderId);
  }

  if (context?.customerId) {
    Sentry.setTag('customer_id', context.customerId);
  }

  Sentry.captureException(error, {
    level: context?.level || 'error',
    extra: context?.extra,
  });
}

export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'info',
  extra?: Record<string, unknown>
) {
  Sentry.captureMessage(message, {
    level,
    extra,
  });
}

export function addBreadcrumb(
  message: string,
  category: string = 'custom',
  data?: Record<string, unknown>
) {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Re-export Sentry for direct access if needed
export { Sentry };

// Export default for convenience
export default {
  initSentry,
  setOrderContext,
  setCustomerContext,
  clearUserContext,
  captureError,
  captureMessage,
  addBreadcrumb,
  SentryErrorBoundary,
  Sentry,
};
